import { useState, useEffect, useCallback } from 'react';
import { agentFeedbackService, type AgentAction, type ActionStatusUpdate, type VisualFeedbackConfig } from '../services/agentFeedbackService.js';

// Simple logger for browser environment
const logger = {
  debug: (...args: any[]) => console.debug('[useAgentFeedback]', ...args)
};

/**
 * Options for the useAgentFeedback hook
 */
export interface UseAgentFeedbackOptions {
  /** Canvas ID to filter actions for (optional) */
  canvasId?: string;
  /** Enable real-time status updates */
  enableRealTimeUpdates?: boolean;
  /** Maximum number of recent actions to track */
  maxRecentActions?: number;
  /** Callback when an action starts */
  onActionStarted?: (action: AgentAction) => void;
  /** Callback when an action completes */
  onActionCompleted?: (action: AgentAction) => void;
  /** Callback when an action fails */
  onActionFailed?: (action: AgentAction) => void;
  /** Callback for real-time status updates */
  onStatusUpdate?: (update: ActionStatusUpdate) => void;
}

/**
 * React hook to consume agent feedback service events
 * 
 * Provides real-time access to agent action states, status updates,
 * and canvas preview data for UI components.
 */
export function useAgentFeedback(options: UseAgentFeedbackOptions = {}) {
  const {
    canvasId,
    enableRealTimeUpdates = true,
    maxRecentActions = 10,
    onActionStarted,
    onActionCompleted,
    onActionFailed,
    onStatusUpdate
  } = options;

  // State for tracking actions and updates
  const [activeActions, setActiveActions] = useState<AgentAction[]>([]);
  const [recentActions, setRecentActions] = useState<AgentAction[]>([]);
  const [lastUpdate, setLastUpdate] = useState<ActionStatusUpdate | null>(null);
  const [statistics, setStatistics] = useState(agentFeedbackService.getStatistics());
  const [config, setConfig] = useState<VisualFeedbackConfig>(agentFeedbackService.getConfig());

  // Refresh active actions
  const refreshActiveActions = useCallback(() => {
    const actions = agentFeedbackService.getActiveActions();
    const filteredActions = canvasId 
      ? actions.filter(action => action.metadata?.canvasId === canvasId)
      : actions;
    
    setActiveActions(filteredActions);
  }, [canvasId]);

  // Refresh recent actions
  const refreshRecentActions = useCallback(() => {
    const actions = canvasId
      ? agentFeedbackService.getCanvasActions(canvasId)
          .filter(action => ['completed', 'failed', 'cancelled'].includes(action.status))
          .slice(-maxRecentActions)
          .reverse()
      : agentFeedbackService.getRecentActions(maxRecentActions);
    
    setRecentActions(actions);
  }, [canvasId, maxRecentActions]);

  // Update statistics
  const refreshStatistics = useCallback(() => {
    setStatistics(agentFeedbackService.getStatistics());
  }, []);

  // Get action by ID
  const getAction = useCallback((actionId: string) => {
    return agentFeedbackService.getAction(actionId);
  }, []);

  // Cancel an active action
  const cancelAction = useCallback((actionId: string, reason?: string) => {
    agentFeedbackService.cancelAction(actionId, reason);
  }, []);

  // Update feedback configuration
  const updateConfig = useCallback((updates: Partial<VisualFeedbackConfig>) => {
    agentFeedbackService.updateConfig(updates);
  }, []);

  // Clear all actions
  const clearActions = useCallback(() => {
    agentFeedbackService.clearActions();
  }, []);

  // Generate canvas preview for an action
  const generatePreview = useCallback((action: AgentAction) => {
    return agentFeedbackService.generateCanvasPreview(action);
  }, []);

  // Check if there are any active actions
  const hasActiveActions = activeActions.length > 0;

  // Check if there are any failed actions
  const hasFailedActions = activeActions.some(action => action.status === 'failed') ||
    recentActions.some(action => action.status === 'failed');

  // Get the current execution status
  const executionStatus = hasActiveActions 
    ? activeActions.some(action => action.status === 'executing') 
      ? 'executing' 
      : 'pending'
    : 'idle';

  // Event handlers
  useEffect(() => {
    if (!enableRealTimeUpdates) return;

    const handleActionCreated = (action: AgentAction) => {
      logger.debug('Action created event received', { actionId: action.id });
      refreshActiveActions();
      refreshStatistics();
    };

    const handleActionStarted = (action: AgentAction) => {
      logger.debug('Action started event received', { actionId: action.id });
      refreshActiveActions();
      onActionStarted?.(action);
    };

    const handleActionCompleted = (action: AgentAction) => {
      logger.debug('Action completed event received', { 
        actionId: action.id, 
        status: action.status 
      });
      
      refreshActiveActions();
      refreshRecentActions();
      refreshStatistics();
      
      if (action.status === 'completed') {
        onActionCompleted?.(action);
      } else if (action.status === 'failed') {
        onActionFailed?.(action);
      }
    };

    const handleStatusUpdate = (update: ActionStatusUpdate) => {
      logger.debug('Status update event received', { 
        actionId: update.actionId, 
        status: update.status 
      });
      
      setLastUpdate(update);
      refreshActiveActions();
      onStatusUpdate?.(update);
    };

    const handleConfigUpdated = (newConfig: VisualFeedbackConfig) => {
      logger.debug('Config updated event received');
      setConfig(newConfig);
    };

    const handleActionsCleared = () => {
      logger.debug('Actions cleared event received');
      setActiveActions([]);
      setRecentActions([]);
      setLastUpdate(null);
      refreshStatistics();
    };

    // Subscribe to events
    agentFeedbackService.on('actionCreated', handleActionCreated);
    agentFeedbackService.on('actionStarted', handleActionStarted);
    agentFeedbackService.on('actionCompleted', handleActionCompleted);
    agentFeedbackService.on('statusUpdate', handleStatusUpdate);
    agentFeedbackService.on('configUpdated', handleConfigUpdated);
    agentFeedbackService.on('actionsCleared', handleActionsCleared);

    // Initial data load
    refreshActiveActions();
    refreshRecentActions();
    refreshStatistics();

    // Cleanup
    return () => {
      agentFeedbackService.off('actionCreated', handleActionCreated);
      agentFeedbackService.off('actionStarted', handleActionStarted);
      agentFeedbackService.off('actionCompleted', handleActionCompleted);
      agentFeedbackService.off('statusUpdate', handleStatusUpdate);
      agentFeedbackService.off('configUpdated', handleConfigUpdated);
      agentFeedbackService.off('actionsCleared', handleActionsCleared);
    };
  }, [
    enableRealTimeUpdates,
    refreshActiveActions,
    refreshRecentActions,
    refreshStatistics,
    onActionStarted,
    onActionCompleted,
    onActionFailed,
    onStatusUpdate
  ]);

  return {
    // State
    activeActions,
    recentActions,
    lastUpdate,
    statistics,
    config,
    
    // Status checks
    hasActiveActions,
    hasFailedActions,
    executionStatus,
    
    // Actions
    getAction,
    cancelAction,
    updateConfig,
    clearActions,
    generatePreview,
    
    // Refresh functions
    refreshActiveActions,
    refreshRecentActions,
    refreshStatistics
  } as const;
}

/**
 * Hook variant focused on a specific action
 */
export function useAgentAction(actionId: string) {
  const [action, setAction] = useState<AgentAction | undefined>(
    agentFeedbackService.getAction(actionId)
  );

  useEffect(() => {
    const handleStatusUpdate = (update: ActionStatusUpdate) => {
      if (update.actionId === actionId) {
        const updatedAction = agentFeedbackService.getAction(actionId);
        setAction(updatedAction);
      }
    };

    const handleActionCompleted = (completedAction: AgentAction) => {
      if (completedAction.id === actionId) {
        setAction(completedAction);
      }
    };

    agentFeedbackService.on('statusUpdate', handleStatusUpdate);
    agentFeedbackService.on('actionCompleted', handleActionCompleted);

    return () => {
      agentFeedbackService.off('statusUpdate', handleStatusUpdate);
      agentFeedbackService.off('actionCompleted', handleActionCompleted);
    };
  }, [actionId]);

  const cancel = useCallback((reason?: string) => {
    agentFeedbackService.cancelAction(actionId, reason);
  }, [actionId]);

  const generatePreview = useCallback(() => {
    return action ? agentFeedbackService.generateCanvasPreview(action) : null;
  }, [action]);

  return {
    action,
    cancel,
    generatePreview,
    isActive: action ? ['pending', 'executing'].includes(action.status) : false,
    isCompleted: action?.status === 'completed',
    isFailed: action?.status === 'failed',
    isCancelled: action?.status === 'cancelled'
  } as const;
}

/**
 * Hook for canvas-specific feedback (filters by canvas ID)
 */
export function useCanvasAgentFeedback(canvasId: string, options: Omit<UseAgentFeedbackOptions, 'canvasId'> = {}) {
  return useAgentFeedback({ ...options, canvasId });
}